import { useEffect, useState } from "react";
import { products, teamMembers } from "../../mocks/products";

export default function Home() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? "bg-white shadow-md" : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://public.readdy.ai/ai/img_res/388b4fee-620c-4df6-838f-bedf3159e850.png" 
                alt="BONABEN S.R.L. Logo" 
                className="h-10 w-10 object-contain"
              />
              <span className="text-xl font-bold text-gray-900">BONABEN S.R.L.</span>
            </div>
            <div className="flex gap-8">
              <button
                onClick={() => scrollToSection("home")}
                className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Home
              </button>
              <button
                onClick={() => scrollToSection("about")}
                className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                About
              </button>
              <button
                onClick={() => scrollToSection("products")}
                className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Products
              </button>
              <button
                onClick={() => scrollToSection("contact")}
                className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Contact
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 w-full h-full">
          <img
            src="https://public.readdy.ai/ai/img_res/6441b9ae3933d522b015738c9dee0bc5.jpg"
            alt="Hero Background"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        
        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center w-full">
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            Innovative iOS Solutions
          </h1>
          <p className="text-xl md:text-2xl text-white/90 mb-8 max-w-3xl mx-auto leading-relaxed">
            We develop cutting-edge applications that enhance user experience and strengthen mobile device security
          </p>
          <div className="flex flex-wrap gap-6 justify-center items-center">
            <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full">
              <i className="ri-shield-check-line text-2xl text-teal-400"></i>
              <span className="text-white font-medium text-sm">Secure & Private</span>
            </div>
            <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full">
              <i className="ri-speed-line text-2xl text-teal-400"></i>
              <span className="text-white font-medium text-sm">High Performance</span>
            </div>
            <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full">
              <i className="ri-apple-line text-2xl text-teal-400"></i>
              <span className="text-white font-medium text-sm">iOS Optimized</span>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">About Us</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              BONABEN S.R.L. is a dedicated team of experts specializing in modern iOS application development
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center mb-20">
            <div className="w-full h-96">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20software%20development%20team%20working%20on%20iOS%20applications%20in%20modern%20office%20environment%2C%20developers%20collaborating%20on%20mobile%20app%20design%20with%20clean%20workspace%2C%20contemporary%20tech%20company%20atmosphere%20with%20minimalist%20aesthetic%2C%20bright%20natural%20lighting%20and%20professional%20setting%2C%20focus%20on%20teamwork%20and%20innovation&width=800&height=600&seq=about-team-bonaben&orientation=landscape"
                alt="Our Team"
                className="w-full h-full object-cover object-top rounded-lg shadow-lg"
              />
            </div>
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-6">Our Mission</h3>
              <p className="text-base text-gray-700 mb-6 leading-relaxed">
                At BONABEN S.R.L., we are committed to creating innovative iOS applications that prioritize user experience, security, and performance. Our team of skilled developers and designers work tirelessly to deliver products that not only meet but exceed the expectations of modern mobile users.
              </p>
              <p className="text-base text-gray-700 mb-6 leading-relaxed">
                With years of experience in the iOS ecosystem, we understand the unique challenges and opportunities that come with mobile development. Our flagship products, including VPN and Cleaner applications, are designed to address real-world needs while maintaining the highest standards of quality and reliability.
              </p>
              <p className="text-base text-gray-700 leading-relaxed">
                We believe in continuous innovation and staying ahead of technological trends to provide our users with the best possible solutions for their mobile devices.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {teamMembers.map((member) => (
              <div key={member.id} className="bg-white p-8 rounded-lg shadow-md hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-6 mx-auto">
                  <i className="ri-team-line text-3xl text-teal-600"></i>
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-2 text-center">{member.name}</h4>
                <p className="text-sm font-medium text-teal-600 mb-4 text-center">{member.role}</p>
                <p className="text-sm text-gray-600 text-center leading-relaxed">{member.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-24 bg-white" data-product-shop>
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Our Products</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Discover our suite of powerful iOS applications designed to enhance your mobile experience
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {products.map((product) => (
              <div
                key={product.id}
                className="bg-gray-50 rounded-lg p-8 hover:shadow-xl transition-all duration-300 border border-gray-100"
              >
                <div className="w-16 h-16 bg-teal-600 rounded-lg flex items-center justify-center mb-6">
                  <i className={`${product.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{product.name}</h3>
                <p className="text-sm text-gray-700 mb-6 leading-relaxed">{product.description}</p>
                <div className="space-y-3">
                  <p className="text-sm font-semibold text-gray-900">Key Features:</p>
                  {product.features.map((feature, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <i className="ri-check-line text-lg text-teal-600"></i>
                      <span className="text-sm text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-16 bg-gradient-to-r from-teal-600 to-cyan-600 rounded-lg p-12 text-center">
            <h3 className="text-3xl font-bold text-white mb-4">Why Choose Our Products?</h3>
            <p className="text-base text-white/90 max-w-3xl mx-auto mb-8 leading-relaxed">
              Our applications are built with cutting-edge technology and designed with user privacy and security as top priorities. We continuously update our products to ensure they remain compatible with the latest iOS versions and deliver optimal performance.
            </p>
            <div className="grid md:grid-cols-4 gap-6 max-w-4xl mx-auto">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <i className="ri-award-line text-4xl text-white mb-3"></i>
                <p className="text-sm font-medium text-white">Award Winning</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <i className="ri-user-heart-line text-4xl text-white mb-3"></i>
                <p className="text-sm font-medium text-white">User Focused</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <i className="ri-refresh-line text-4xl text-white mb-3"></i>
                <p className="text-sm font-medium text-white">Regular Updates</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <i className="ri-customer-service-line text-4xl text-white mb-3"></i>
                <p className="text-sm font-medium text-white">24/7 Support</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Contact Us</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Get in touch with our team for inquiries, support, or partnership opportunities
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            <div className="w-full h-96">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20office%20building%20exterior%20in%20Argentina%20with%20contemporary%20architecture%2C%20professional%20business%20headquarters%20with%20clean%20lines%20and%20glass%20facade%2C%20urban%20corporate%20environment%20with%20clear%20sky%2C%20architectural%20photography%20with%20professional%20lighting%20and%20sharp%20details%2C%20representing%20technology%20company%20location&width=800&height=600&seq=contact-office-bonaben&orientation=landscape"
                alt="Our Office"
                className="w-full h-full object-cover object-top rounded-lg shadow-lg"
              />
            </div>

            <div className="space-y-6">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-xl text-teal-600"></i>
                  </div>
                  <div>
                    <h4 className="text-base font-bold text-gray-900 mb-2">Address</h4>
                    <p className="text-sm text-gray-700">SANTA FE 2626</p>
                    <p className="text-sm text-gray-700">Marcos Juárez, X2580EIE</p>
                    <p className="text-sm text-gray-700">Argentina</p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-xl text-teal-600"></i>
                  </div>
                  <div>
                    <h4 className="text-base font-bold text-gray-900 mb-2">Email</h4>
                    <p className="text-sm text-gray-700">info@bonabensrl.com</p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-xl text-teal-600"></i>
                  </div>
                  <div>
                    <h4 className="text-base font-bold text-gray-900 mb-2">Number</h4>
                    <p className="text-sm text-gray-700">+1 6823563620</p>
                  </div>
                </div>
              </div>

              <div className="bg-teal-600 p-8 rounded-lg text-center">
                <i className="ri-time-line text-4xl text-white mb-4"></i>
                <h4 className="text-lg font-bold text-white mb-2">Business Hours</h4>
                <p className="text-sm text-white/90">Monday - Friday: 9:00 AM - 6:00 PM</p>
                <p className="text-sm text-white/90">Saturday - Sunday: Closed</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-teal-600 py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-3">
              <img 
                src="https://public.readdy.ai/ai/img_res/388b4fee-620c-4df6-838f-bedf3159e850.png" 
                alt="BONABEN S.R.L. Logo" 
                className="h-10 w-10 object-contain"
              />
              <span className="text-xl font-bold text-white">BONABEN S.R.L.</span>
            </div>
            <div className="text-center">
              <p className="text-sm text-white/90">
                &copy; {new Date().getFullYear()} BONABEN S.R.L. All rights reserved.
              </p>
              <a 
                href="https://readdy.ai/?ref=logo" 
                className="text-sm text-white/80 hover:text-white transition-colors mt-2 inline-block whitespace-nowrap cursor-pointer"
                rel="nofollow"
              >
                Powered by Readdy
              </a>
            </div>
            <div className="flex gap-4">
              <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors cursor-pointer">
                <i className="ri-linkedin-line text-lg text-white"></i>
              </div>
              <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors cursor-pointer">
                <i className="ri-twitter-x-line text-lg text-white"></i>
              </div>
              <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors cursor-pointer">
                <i className="ri-facebook-line text-lg text-white"></i>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
